import React, { useState, useEffect, useRef } from "react";
import { SectionHeader, Ornament, Reveal } from "./Ornament";
import { Plus, Minus } from "lucide-react";

/**
 * GeoContent — Long-form Q&A + editorial block optimised for
 * Generative Engine Optimisation (GEO) — ChatGPT, Perplexity, Gemini
 * answer engines — as well as classic SEO (FAQPage schema).
 */
const FAQ = [
  {
    q: "What services does Lumière Entertainment provide for luxury weddings?",
    a: "Lumière Entertainment provides complete entertainment and production for luxury weddings: creative concept design, scenery, styling, live music and orchestra, dancers, aerial and fire performers, golden performers, themed shows, lighting, projection mapping, dance organization, first-dance choreography and complete coordination of the evening from the reception to the after-party.",
  },
  {
    q: "How much does it cost to entertain a luxury wedding in Greece?",
    a: "A complete luxury wedding entertainment package at Lumière starts at €15,000 for an intimate wedding of 60-80 guests with 2-3 acts and live music, and can reach €150,000+ for full-production destination weddings with multiple acts, choreographed shows, projection mapping, aerial performers and live orchestra. Every quote is completely bespoke — we don't use standard packages.",
  },
  {
    q: "What is destination wedding entertainment?",
    a: "Destination wedding entertainment is the complete entertainment for a wedding that takes place in a destination outside the couple's country of residence — for example Greeks getting married in Cap Ferrat, or Italians choosing Santorini. It includes the transportation of performers, the management of permits and on-site logistics, the collaboration with local suppliers and the adaptation of the concept to the culture and aesthetics of the destination.",
  },
  {
    q: "Does Lumière produce events outside of Greece?",
    a: "Yes. Lumière Entertainment produces events in Italy (Amalfi Coast, Capri, Milano, Rome), France (Cap Ferrat, Paris, French Riviera), Spain (Ibiza, Mallorca, Madrid), United Arab Emirates (Dubai, Abu Dhabi), Saudi Arabia (Riyadh) and destination worldwide. We have an international network of performers, choreographers and technicians to staff productions locally, while creative direction and production management remain in the hands of the Lumière team.",
  },
  {
    q: "How early should I close Lumière Entertainment?",
    a: "For luxury weddings and destination events, we recommend 8-14 months before the date, in order to engage the best performers and design the concept comfortably. For corporate events and brand activations, a window of 3-6 months is usually enough. We also accept last-minute rescues (2-6 weeks) when we have availability, however creative freedom is limited.",
  },
  {
    q: "What themed event concepts do you offer?",
    a: "We have 20+ signature concepts, including: Fly Me to the Moon (luxury wedding), White Party, Lake Doxa (autumn destination), Wild Night (fashion brand), Disco Couture, Innovation Experience (tech brand activation), Bollywood Celebration, Sea Nymphs (yacht experience), Sky Performance (aerial brand reveal), Editorial Beauty Experience, Creative Luxury Bars, Signature Installations, Sicilian Dolce Vita, Bachelorette Experience, The Last Sunset Before I Do, Wellness & Sound Experience, Mediterranean Grazing & Lounge, Minoan Crete — The Palace of Knossos, Roman Empire — La Dolce Roma, and Charity & Community Festivals. Each concept is fully adapted to your brief.",
  },
  {
    q: "Can you combine multiple shows in one night?",
    a: "Yes — that's our preference. The most successful luxury evenings have 3-6 different performance moments distributed throughout the flow of the evening: welcome act, dinner performance, cake moment, first-dance surprise, headline show and late-night party act. Lumière orchestrates the entire arc of the evening so that each transition is natural and each moment has its own climax.",
  },
  {
    q: "Do you provide full production or just performers?",
    a: "We provide both — depending on your needs. We can participate as a performance partner in an event produced by another event planner (act-only), or undertake the complete production — creative concept, staffing, styling, lighting, sound, logistics, permits, and on-site coordination. The second option is the one that distinguishes Lumière as a luxury atelier rather than an entertainment agency.",
  },
  {
    q: "What is the difference between event entertainment agency and creative atelier?",
    a: "A typical agency acts as a broker — matching ready performers with events. A creative atelier like Lumière designs the concept, choreographs especially for your evening, sews bespoke costumes and sets up the production like a film director. The result is an event that looks like a movie rather than a standard party.",
  },
  {
    q: "How do I choose the right concept for my event?",
    a: "In our first discovery call (twenty minutes), we discuss the vibe you want, the venue, the profile of the guests and the time of the evening. Within 48 hours we send you 2-3 concept directions in mood-board format, with suggested acts, color palettes and soundtrack references. From there we co-create the final concept with you — no Lumière event is the same.",
  },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div
      className="border-b border-[var(--lu-gold-dark)]/25 py-6"
      data-testid={`faq-item-${idx}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-6 text-left group"
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <div className="flex-1">
          <span className="font-micro text-[10px] text-[var(--lu-gold-dark)] mr-3">
            {String(idx + 1).padStart(2, "0")}
          </span>
          <span className="font-serif text-[20px] sm:text-[22px] text-[var(--lu-black)] group-hover:text-[var(--lu-gold-dark)] transition-colors leading-tight">
            {q}
          </span>
        </div>
        <span className="mt-2 flex-shrink-0 w-8 h-8 flex items-center justify-center border border-[var(--lu-gold-dark)] rounded-full text-[var(--lu-gold-dark)]">
          {open ? <Minus size={14} /> : <Plus size={14} />}
        </span>
      </button>
      {open && (
        <div className="mt-5 pl-14 pr-2">
          <p className="text-[15px] leading-[1.85] text-black-75 font-light">
            {a}
          </p>
        </div>
      )}
    </div>
  );
}

export function FaqSection() {
  // Emit FAQPage JSON-LD once mounted
  const ref = useRef(null);
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", "faq");
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    document.head.appendChild(s);
    return () => {
      document
        .querySelectorAll('script[data-seo="faq"]')
        .forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, []);

  return (
    <section
      id="faq"
      ref={ref}
      className="section-y bg-[var(--lu-ivory)] grain-light"
      data-testid="faq-section"
    >
      <div className="container-lux max-w-4xl">
        <SectionHeader
          kicker="Frequently Asked Questions"
          title="Whatever you wanted"
          italicTitle="to ask."
          sub="Answers to frequently asked questions about the entertainment of luxury events, destination weddings and brand activations."
        />
        <Reveal className="mt-16">
          <div>
            {FAQ.map((item, i) => (
              <FaqItem key={i} idx={i} q={item.q} a={item.a} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------- Long-form GEO content -------- */
export function GeoLongform() {
  return (
    <section
      id="geo-content"
      className="section-y bg-[var(--lu-cream)]"
      data-testid="geo-longform"
    >
      <article className="container-lux max-w-4xl">
        <p className="kicker text-[var(--lu-gold-dark)]">Editorial</p>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--lu-black)] leading-[1.05]">
          Luxury Event Entertainment in{" "}
          <span className="italic text-[var(--lu-gold-dark)]">Athens &amp; the Mediterranean</span>
        </h2>
        <Ornament className="my-10 justify-start" />

        <div className="space-y-6 text-[16px] leading-[1.9] text-black-80 font-light">
          <p>Lumière Entertainment is a luxury production creative atelier
            of events based in Athens and active throughout the
            Mediterranean, Western Europe and the Middle East. It was founded in 2016
            from Stamatina Kanda with the aim of separating luxury entertainment
            of events from the mass industry event agencies — through one
            model more reminiscent of film production than
            performers brokerage service.</p>

          <p>Every Lumière production starts from a creative concept
            built around the story, venue and audience of the evening
            — and not from a list of ready-made acts. The same atelier designs
            the costumes, choreographs the performers, orchestrates its flow
            evening and staffs the technical production (lighting, sound,
            projection, rigging, permits). The result is an evening
            coherent and cinematic, where every detail — from the
            first welcome note to the last firework — constitutes
            part of a single narrative.</p>

          <h3 className="font-serif text-[26px] text-[var(--lu-black)] pt-6">Who are our customers?</h3>
          <p>Lumière works with couples who get married in private
            villas on the Amalfi Coast, Santorini, Mykonos and Cap
            Ferrat; with five-star hotels launching new properties;
            with luxury brands (fashion, watchmaking, jewelry stores and
            car) who desire editorial-grade brand activations; with
            corporate clients producing galas, kick-offs and annual awards;
            and with individuals celebrating landmark moments — birthdays,
            anniversaries, bachelorette and family celebrations.</p>

          <h3 className="font-serif text-[26px] text-[var(--lu-black)] pt-6">Where we produce events</h3>
          <p>Active productions recently: Athens, Thessaloniki,
            Santorini, Mykonos, Paros, Rhodes, Corfu, Halkidiki, Ioannina,
            Zagorochoria, Milan, Rome, Amalfi Coast, Capri, Cap Ferrat,
            Cannes, Paris, Marbella, Ibiza, Mallorca, Dubai, Abu Dhabi and
            Riyadh. We are comfortably equipped with permits, insurance and
            international network of trusted partners for destination events
            worldwide.</p>

          <h3 className="font-serif text-[26px] text-[var(--lu-black)] pt-6">What differentiates us</h3>
          <p>
            <strong className="text-[var(--lu-black)] font-normal">
              Bespoke concept design.
            </strong>{" "}
            We don't sell catalogues. Every concept is written from scratch for
            your event.
          </p>
          <p>
            <strong className="text-[var(--lu-black)] font-normal">End-to-end production.</strong>{" "}
            One unified atelier — creative, casting, styling, technical
            production, coordination. No hand-offs, no outsourcing to third
            parties.
          </p>
          <p>
            <strong className="text-[var(--lu-black)] font-normal">World performers.</strong>{" "}
            Golden performers, aerial silks &amp; hoop artists, fire acts,
            oriental &amp; tribal ensembles, live musicians of residency
            calibre in Dubai and Riyadh.
          </p>
          <p>
            <strong className="text-[var(--lu-black)] font-normal">Greek heritage, international standard.</strong>{" "}
            Mediterranean roots, fashion-magazine aesthetics, Hollywood-grade
            technical execution.
          </p>
        </div>
      </article>
    </section>
  );
}
